import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  ArrowRight, 
  ArrowLeft,
  Briefcase,
  MapPin,
  Scale,
  Sparkles,
  Plus,
  X
} from 'lucide-react';

/**
 * HP.4 Guided Input: Career Decision Wizard
 * 3 steps: Current Situation → What You're Considering → Decision Factors
 */

const STEPS = [
  { id: 'current', title: 'Where You Are Now', subtitle: 'Your current role and situation', icon: Briefcase },
  { id: 'considering', title: 'What You\'re Considering', subtitle: 'Where you might go next', icon: MapPin },
  { id: 'factors', title: 'What Matters Most', subtitle: 'Your decision criteria', icon: Scale },
];

const FACTOR_OPTIONS = [
  'Career growth & progression',
  'Compensation & benefits',
  'Work-life balance',
  'Company culture & values',
  'Learning & skill development',
  'Team & manager quality',
  'Job security & stability',
  'Autonomy & ownership',
  'Impact & meaning',
  'Location & flexibility',
];

const CareerGuidedInput = ({ onComplete, onCancel, accentColor = '#06b6d4' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    role: '',
    tenure: '',
    whats_good: '',
    whats_not: '',
    considering: '',
    dream_direction: '',
    selected_factors: [],
    dealbreaker: '',
    optional_paste: '',
  });
  const [showOptionalPaste, setShowOptionalPaste] = useState(false);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleFactor = (factor) => {
    setFormData(prev => {
      const current = prev.selected_factors;
      if (current.includes(factor)) {
        return { ...prev, selected_factors: current.filter(f => f !== factor) };
      }
      if (current.length >= 5) return prev;
      return { ...prev, selected_factors: [...current, factor] };
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.role.trim().length >= 2 && formData.whats_not.trim().length >= 10;
      case 1:
        return formData.considering.trim().length >= 10;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const contextParts = [
        `Currently: ${formData.role}`,
        formData.tenure ? `Tenure: ${formData.tenure}` : '',
        formData.whats_good ? `What's working: ${formData.whats_good}` : '',
        `What's not working: ${formData.whats_not}`,
      ].filter(Boolean).join('. ');

      const achievementParts = [
        `Considering: ${formData.considering}`,
        formData.dream_direction ? `Ideal direction: ${formData.dream_direction}` : '',
        formData.selected_factors.length > 0 ? `Key factors: ${formData.selected_factors.join(', ')}` : '',
        formData.dealbreaker ? `Dealbreaker: ${formData.dealbreaker}` : '',
      ].filter(Boolean);

      onComplete({
        role: formData.role.trim(),
        context: contextParts,
        achievements: achievementParts,
        challenges: formData.whats_not.trim(),
        growth: formData.dream_direction.trim(),
        goals: formData.selected_factors.join(', '),
        optional_paste: formData.optional_paste.trim(),
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const StepIcon = STEPS[currentStep].icon;

  return (
    <div data-testid="career-guided-input" className="max-w-2xl mx-auto">
      {/* Progress Bar */}
      <div data-testid="career-progress-bar" className="mb-8">
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>Step {currentStep + 1} of {STEPS.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: accentColor }} />
        </div>
      </div>

      {/* Step Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}20` }}>
          <StepIcon className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">{STEPS[currentStep].title}</h3>
          <p className="text-sm text-gray-400">{STEPS[currentStep].subtitle}</p>
        </div>
      </div>

      {/* Step Content */}
      <div className="space-y-4">
        {currentStep === 0 && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Your current role</label>
              <input
                data-testid="career-role-input"
                type="text"
                value={formData.role}
                onChange={(e) => updateField('role', e.target.value)}
                placeholder="e.g. IT Manager, Senior Developer, Product Lead"
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': accentColor }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">How long have you been in this role?</label>
              <input
                data-testid="career-tenure-input"
                type="text"
                value={formData.tenure}
                onChange={(e) => updateField('tenure', e.target.value)}
                placeholder="e.g. 2 years, 6 months (optional)"
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': accentColor }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">What's working well?</label>
              <textarea
                data-testid="career-good-input"
                value={formData.whats_good}
                onChange={(e) => updateField('whats_good', e.target.value)}
                placeholder="The team, the pay, the learning, the stability... (optional)"
                rows={2}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                style={{ '--tw-ring-color': accentColor }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">What's pushing you to think about leaving?</label>
              <textarea
                data-testid="career-not-good-input"
                value={formData.whats_not}
                onChange={(e) => updateField('whats_not', e.target.value)}
                placeholder="Stagnation, bad management, lack of growth, politics, burnout, undervalued, bored..."
                rows={3}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                style={{ '--tw-ring-color': accentColor }}
              />
              <p className="text-xs text-gray-500 mt-1">Min 10 characters — be honest, this is just for you</p>
            </div>
          </>
        )}

        {currentStep === 1 && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">What are you considering?</label>
              <textarea
                data-testid="career-considering-input"
                value={formData.considering}
                onChange={(e) => updateField('considering', e.target.value)}
                placeholder="Leaving for another company? Going freelance? A completely different career? Staying and trying to fix things? Moving to a different team?"
                rows={4}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                style={{ '--tw-ring-color': accentColor }}
              />
              <p className="text-xs text-gray-500 mt-1">Min 10 characters — describe what you're thinking about</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">If you could design your ideal next step, what would it look like?</label>
              <textarea
                data-testid="career-dream-input"
                value={formData.dream_direction}
                onChange={(e) => updateField('dream_direction', e.target.value)}
                placeholder="More autonomy, a leadership role, a startup, remote work, better pay, more meaningful work... (optional)"
                rows={3}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                style={{ '--tw-ring-color': accentColor }}
              />
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">What matters most to you? (Pick up to 5)</label>
              <div className="flex flex-wrap gap-2">
                {FACTOR_OPTIONS.map((factor) => {
                  const isSelected = formData.selected_factors.includes(factor);
                  return (
                    <button
                      key={factor}
                      data-testid={`career-factor-${factor.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      onClick={() => toggleFactor(factor)}
                      className="px-3 py-2 rounded-lg text-sm border transition-all"
                      style={{
                        backgroundColor: isSelected ? `${accentColor}20` : 'transparent',
                        borderColor: isSelected ? accentColor : '#374151',
                        color: isSelected ? accentColor : '#9ca3af',
                      }}
                    >
                      {factor}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">{formData.selected_factors.length}/5 selected (optional)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Is there a dealbreaker?</label>
              <input
                data-testid="career-dealbreaker-input"
                type="text"
                value={formData.dealbreaker}
                onChange={(e) => updateField('dealbreaker', e.target.value)}
                placeholder="e.g. 'If I don't get promoted by Q3, I'm out' (optional)"
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': accentColor }}
              />
            </div>
            {/* Optional paste */}
            <div className="pt-2">
              {!showOptionalPaste ? (
                <button
                  onClick={() => setShowOptionalPaste(true)}
                  className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Have a job description to paste? (optional)
                </button>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Paste a job description you're interested in (optional)</label>
                  <textarea
                    data-testid="career-paste-input"
                    value={formData.optional_paste}
                    onChange={(e) => updateField('optional_paste', e.target.value)}
                    placeholder="Paste a job listing, role description, or anything relevant..."
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                    style={{ '--tw-ring-color': accentColor }}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-4 border-t border-gray-800">
        <div>
          {currentStep > 0 ? (
            <Button data-testid="career-back-btn" variant="ghost" onClick={handleBack} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
          ) : (
            <Button data-testid="career-cancel-btn" variant="ghost" onClick={onCancel} className="text-gray-400 hover:text-white">
              Cancel
            </Button>
          )}
        </div>
        <Button
          data-testid="career-next-btn"
          onClick={handleNext}
          disabled={!canProceed()}
          className="text-white font-medium px-6"
          style={{ backgroundColor: canProceed() ? accentColor : undefined }}
        >
          {currentStep < STEPS.length - 1 ? (
            <>Next <ArrowRight className="w-4 h-4 ml-1.5" /></>
          ) : (
            <>Analyse My Options <Sparkles className="w-4 h-4 ml-1.5" /></>
          )}
        </Button>
      </div>
    </div>
  );
};

export default CareerGuidedInput;
